import { AxiosInstance, AxiosResponse } from 'axios';
import config from '../../config';
import {
  CsvExportOptionsEnum,
  EProjectStatus,
  IExportCSV,
  IImportCsv,
  IImportCsvResult,
  IPageableResponse,
  IProject,
  ProjectDetails,
  RefreshTypeEnum
} from '@recone/models';
import { IProjectFromFiltersInputType } from 'components/SearchCriteria/SearchCriteria';

const GET_PROJECTS_PATH = 'project/list';
const CREATE_PROJECT_PATH = 'project/add';
const CREATE_SAVED_TAB = 'project/saved-tabs';
const UPDATE_PROJECT_PATH = 'project/update';
const DELETE_PROJECT_PATH = 'project/remove';
const GET_PROJECT = 'project/byId';
const IMPORT_CSV_PROJECT = 'project/import';
const EXPORT_JSON_PROJECT = 'project/export';
const EXPORT_CSV_PROJECT = 'project/export-csv';
const GET_PROJECT_EMAILS = 'project/request-emails';
const SET_DEFAULT_CRITERIA = 'project/set-default-criteria';
const UPLOAD_CV = 'project/upload-cv';
const GET_ONE_CV = 'project/get-one-cv';
const ADD_FROM_CRITERIA = 'project/add-from-criteria';
const SCRAPE_STATUS = 'project/scrape-status';
const APPROVE = 'project/approve';
const MIGRATE_PROJECTS = 'project/migrate/project';
const MIGRATE_PROFILES = 'project/migrate/profiles';
const UPDATE_PROJECT_STATUS_PATH = `${config.service.BASE_URL}/project/update-status`;

export const EXPORT_CSV_URL = `${config.service.BASE_URL}/${EXPORT_CSV_PROJECT}`;

export const getProjects = (
  axiosInstance: AxiosInstance,
  page = 0,
  limit = config.service.QUERY_DEFAULT_LIMIT,
  status: EProjectStatus
): Promise<AxiosResponse<IPageableResponse<IProject>>> =>
  axiosInstance.get(`${config.service.BASE_URL}/${GET_PROJECTS_PATH}?page=${page}&limit=${limit}&status=${status}`);

export const createProject = (
  axiosInstance: AxiosInstance,
  projectName: string,
  description: string,
  candidatesNumber: number,
  status?: EProjectStatus
): Promise<AxiosResponse<{ data: IProject; message: string }>> =>
  axiosInstance.post(`${config.service.BASE_URL}/${CREATE_PROJECT_PATH}`, {
    project: { name: projectName, description, candidatesNumber, status: status }
  });

export const addFromCriteria = (
  axiosInstance: AxiosInstance,
  data: IProjectFromFiltersInputType
): Promise<AxiosResponse<IProject>> =>
  axiosInstance.post(`${config.service.BASE_URL}/${ADD_FROM_CRITERIA}`, { ...data });

export const updateProject = (
  axiosInstance: AxiosInstance,
  project: Partial<IProject>
): Promise<AxiosResponse<IProject>> =>
  axiosInstance.put(`${config.service.BASE_URL}/${UPDATE_PROJECT_PATH}`, { project: project });

export const deleteProject = (axiosInstance: AxiosInstance, projectId: string): Promise<AxiosResponse<IProject>> =>
  axiosInstance.delete(`${config.service.BASE_URL}/${DELETE_PROJECT_PATH}/${projectId}`);

export const getProject = (axiosInstance: AxiosInstance, projectId: string): Promise<AxiosResponse<ProjectDetails>> =>
  axiosInstance.get(`${config.service.BASE_URL}/${GET_PROJECT}/${projectId}`);

export const importCsvToProject = (
  axiosInstance: AxiosInstance,
  payload: IImportCsv[],
  projectId: string
): Promise<AxiosResponse<IImportCsvResult>> =>
  axiosInstance.post(`${config.service.BASE_URL}/${IMPORT_CSV_PROJECT}`, { payload: payload, projectId: projectId });

export const exportJsonFromProject = (
  axiosInstance: AxiosInstance,
  id: string,
  exportOptions: CsvExportOptionsEnum
): Promise<AxiosResponse<IExportCSV>> =>
  axiosInstance.post(`${config.service.BASE_URL}/${EXPORT_JSON_PROJECT}/${id}`, {
    id: id,
    exportOptions
  });

export const getProjectEmails = (axiosInstance: AxiosInstance, id: string): Promise<AxiosResponse<IImportCsv[]>> =>
  axiosInstance.post(`${config.service.BASE_URL}/${GET_PROJECT_EMAILS}/${id}`, {});

export const setDefaultCriteria = (
  axiosInstance: AxiosInstance,
  projectId: string,
  criteriaId: string | null
): Promise<AxiosResponse<IProject>> =>
  axiosInstance.put(`${config.service.BASE_URL}/${SET_DEFAULT_CRITERIA}`, { projectId, criteriaId });

export const createSavedTab = (
  axiosInstance: AxiosInstance,
  parentProject: string,
  name: string
): Promise<AxiosResponse<IProject>> =>
  axiosInstance.post(`${config.service.BASE_URL}/${CREATE_SAVED_TAB}`, { project: { name, parentProject } });

export const deleteSavedTab = (
  axiosInstance: AxiosInstance,
  projectId: string,
  savedTabId: string
): Promise<AxiosResponse<IProject>> =>
  axiosInstance.delete(`${config.service.BASE_URL}/${CREATE_SAVED_TAB}/${projectId}/${savedTabId}`);

export const attachCvToCandidate = (axiosInstance: AxiosInstance, formData: FormData): Promise<AxiosResponse> =>
  axiosInstance.post(`${config.service.BASE_URL}/${UPLOAD_CV}`, formData, {});

export const getCandidateCv = (axiosInstance: AxiosInstance, profileId: string): Promise<AxiosResponse> =>
  axiosInstance.get(`${config.service.BASE_URL}/${GET_ONE_CV}`, {
    params: { profileForTeamId: profileId }
  });

export const getScrapeStatus = (axiosInstance: AxiosInstance, projectId: string): Promise<AxiosResponse> =>
  axiosInstance.get(`${config.service.BASE_URL}/${SCRAPE_STATUS}/${projectId}`);

export const approveProject = (
  axiosInstance: AxiosInstance,
  projectId: string,
  force?: boolean
): Promise<AxiosResponse> => axiosInstance.patch(`${config.service.BASE_URL}/${APPROVE}/${projectId}`, { force });

export const migrateProjects = (
  axiosInstance: AxiosInstance,
  projectIds: string[],
  sourceTeamId: string,
  targetTeamId: string,
  move?: boolean
): Promise<AxiosResponse> =>
  axiosInstance.post(`${config.service.BASE_URL}/${MIGRATE_PROJECTS}`, {
    projectIds,
    sourceTeamId,
    targetTeamId,
    move
  });

export const migrateProfiles = (
  axiosInstance: AxiosInstance,
  sourceProjectIds: string[],
  targetProjectId: string,
  move?: boolean,
  deleteOnMove?: boolean
): Promise<AxiosResponse> =>
  axiosInstance.post(`${config.service.BASE_URL}/${MIGRATE_PROFILES}`, {
    sourceProjectIds,
    targetProjectId,
    move,
    deleteOnMove
  });

export const updateProjectStatus = (axiosInstance: AxiosInstance, projectId: string, status: EProjectStatus) =>
  axiosInstance.patch(`${UPDATE_PROJECT_STATUS_PATH}/${projectId}`, { status });

export const resourceIds = (
  axiosInstance: AxiosInstance,
  id: string,
  resource: RefreshTypeEnum
): Promise<AxiosResponse> =>
  axiosInstance.get(
    `${config.service.BASE_URL}/project/${id}/resources`,

    {
      params: {
        resource: resource
      }
    }
  );
